import {
  deriveKey,
  deriveKeyWithSalt,
  encryptString,
  kekFromMk,
} from "@lib/crypto/crypto";
import { isUserAuthenticated } from "./auth";
import { useAuthState } from "@/zustand/AuthState";
import { updateEncryptedPassword } from "@/server/services/recoveryKeyService";

const setEncryptedPassword = useAuthState.getState().setEncryptedPassword;

export const isPasswordValid = (password: string): boolean => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    // Used inside a zod `.refine`, which must RETURN a boolean — never throw.
    // Throwing here escapes uncaught out of safeParse and crashes the app
    // (e.g. on /login with no in-memory auth fingerprint). Fail validation instead.
    return false;
  }

  const { username, dk, kdfVersion, kdfSalt } = userFingerPrint;

  // `dk` is the private-key KEK: for v2/v3 that is HKDF(mk, kek), for v1 it is
  // `mk` itself. v3 derives mk from the stored random salt; v1/v2 from the
  // username. Derive the same to compare, so the check holds under all schemes.
  // deriveKeyWithSalt throws on a malformed salt; this fn is used in a zod
  // `.refine` and MUST NOT throw (see the fingerprint guard above), so treat a
  // derivation failure as an invalid password rather than letting it escape.
  let mk: string;
  try {
    mk =
      kdfVersion >= 3
        ? deriveKeyWithSalt(password, kdfSalt)
        : deriveKey(password, username);
  } catch {
    return false;
  }
  const expected = kdfVersion >= 2 ? kekFromMk(mk) : mk;
  return expected === dk;
};

export const encryptPasswordWithRecoveryKey = async (
  recoveryKey: string,
  password: string,
) => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!recoveryKey) {
    throw new Error("Recovery key not provided.");
  }

  const { username } = userFingerPrint;

  const derivedKey = deriveKey(recoveryKey, username);

  const payload = JSON.stringify({
    password: password,
  });
  const encryptedPassword = await encryptString(payload, derivedKey);

  try {
    await updateEncryptedPassword({
      encryptedPassword: encryptedPassword,
    });
    setEncryptedPassword(encryptedPassword);
  } catch {
    throw new Error("Failed to set recovery key.");
  }
};
